import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import {
  CHART_DISPLAY_TYPES,
  ChartDisplayType,
  NUMERIC_AGGREGATIONS,
  NumericAggregation,
  Period,
  PERIODS,
} from "@/shared/domain/metrics";
import {
  savedFiltersSchema,
  type SavedFilters,
} from "@/shared/domain/views/savedFilters";

/**
 * The persisted `dashboard_definition` schema: the types stored in a
 * `DashboardRecord`'s JSONB, the enums those types range over, and the
 * structural validator (`isDashboardDefinition`) that gates anything loaded
 * from outside the running app. Layout and URL handling live in the
 * MetricsDashboard feature; this module imports only from other shared
 * modules, so shared code never depends on a feature.
 */

// Required to validate a custom string
dayjs.extend(customParseFormat);

/** The dashboard_definition schema version. */
export const DASHBOARD_SCHEMA_VERSION = 1;

/**
 * The preset time windows a dashboard can aggregate over. Part of the
 * persisted schema: a stored definition's `range` is either one of these
 * values or a {@link CustomRange}.
 *
 * `short` is the abbreviated label for the segmented range control, which has
 * far less room than the menu the full `label` was written for.
 */
export const TIME_RANGES = [
  { value: "30d", label: "Last 30 days", short: "30D", days: 30 },
  { value: "90d", label: "Last 90 days", short: "3M", days: 90 },
  { value: "365d", label: "Last year", short: "1Y", days: 365 },
  { value: "all", label: "All time", short: "All", days: 3650 },
] as const;

export type PresetRange = (typeof TIME_RANGES)[number]["value"];

/**
 * An explicit window the user picked, as two INCLUSIVE calendar dates.
 *
 * Dates, not instants: no aggregation period is finer than a calendar day
 * (see `AggregationPeriod`), so a time of day could not change any result.
 * The stored values are exactly what the user chose. `TimeRange.fromDates`
 * adds the `+1 day` that makes the end exclusive when resolving, and never
 * writes it back here.
 */
export interface CustomRange {
  kind: "custom";
  /** `YYYY-MM-DD`, inclusive. */
  start: string;
  /** `YYYY-MM-DD`, inclusive. */
  end: string;
}

/**
 * A preset token or an explicit window. Modelled as a union rather than a
 * sentinel plus optional sibling fields so that a custom range without bounds
 * is unrepresentable, and so selecting a preset structurally discards any
 * previous bounds — the two are mutually exclusive by construction.
 */
export type TimeRangeMode = PresetRange | CustomRange;

/**
 * The persisted `dashboard_definition` shape. Anything else (corrupt storage,
 * a hand-edited record) is simply invalid: loaders gate on
 * {@link isDashboardDefinition} and fall back to their invalid-input path.
 */
export interface DashboardDefinition {
  schema_version: typeof DASHBOARD_SCHEMA_VERSION;
  range: TimeRangeMode;
  period: Period;
  /** Narrows every card's query. Absent means unfiltered. */
  filters?: SavedFilters;
  sections: SectionConfig[];
  cards: CardConfig[];
}

export type CardKind = "trend" | "chart";

/**
 * A card's explicit cell placement on its section's master canvas: a 1-based
 * cell coordinate (`row`, `col`) and a size in cells (`w`×`h`), ALWAYS in
 * master-canvas coordinates (`MASTER_COLS` columns wide — see
 * `placedLayout.ts`). Narrower screens render a compressed projection of the
 * master layout; they never store their own coordinate space. Cards without a
 * placement flow automatically after the placed ones.
 */
export interface Placement {
  row: number;
  col: number;
  w: number;
  h: number;
}

/** A dashboard section: a titled sub-canvas, arranged horizontally by index. */
export interface SectionConfig {
  id: string;
  title: string;
}

/** Fields shared by every card kind: the section it belongs to (missing means
 *  the first section) and its explicit placement (missing means auto-flow). */
export interface PlacedCardBase {
  placement?: Placement;
  section?: string;
}

/** A single-metric KPI card (a 1×1 "trend tile"). */
export interface TrendCardConfig extends PlacedCardBase {
  id: string;
  kind: "trend";
  /** Null means "fall back to an available metric definition". */
  metric: string | null;
  aggregation: NumericAggregation;
}

/**
 * A chart card that plots a `NumericAggregation` of its metric within each
 * bucket of the dashboard's `Period`.
 */
export interface AggregatedChartCardConfig extends PlacedCardBase {
  id: string;
  kind: "chart";
  /** Null means "fall back to the first available metric definition". */
  metric: string | null;
  aggregation: NumericAggregation;
  display: ChartDisplayType;
  /**
   * Split the chart into one series per distinct value of this field, named
   * by the same entity-prefixed wire path the dashboard's filters speak
   * (`device.device_id`, `session.custom.<name>`, `device.custom.<name>`).
   *
   * Absent means one series over everything the card's query matches — what
   * every chart did before this field existed, and what every dashboard saved
   * before it still says.
   */
  group_by?: string;
}

/**
 * A chart card that plots the metric's own values — one point per session —
 * over the dashboard's time range, unbucketed.
 *
 * It carries NO `aggregation` (there is none to apply) and NO `display` (bar
 * degenerates to hairlines on the time-proportional linear x-axis raw points
 * need, so raw is always a line). Their ABSENCE is the discriminant between
 * the two chart variants. The `?: undefined` declarations have no runtime
 * representation — the keys are simply not there — but they let TypeScript
 * narrow in BOTH directions off `card.aggregation === undefined`.
 */
export interface RawChartCardConfig extends PlacedCardBase {
  id: string;
  kind: "chart";
  /** Null means "fall back to the first available metric definition". */
  metric: string | null;
  aggregation?: undefined;
  display?: undefined;
  /**
   * Grouping is NOT part of what the two variants disagree about. See
   * {@link AggregatedChartCardConfig.group_by} — the difference is only in
   * how the split is computed: an aggregated card asks the server to group
   * its buckets, while a raw card's rows come back one per session and are
   * split client-side, so a raw card can only be grouped by a field the row
   * itself carries (see `rawGroupableFieldDefinitions`).
   */
  group_by?: string;
}

export type ChartCardConfig = AggregatedChartCardConfig | RawChartCardConfig;

export type CardConfig = TrendCardConfig | ChartCardConfig;

export function isPresetRange(range: TimeRangeMode): range is PresetRange {
  return typeof range === "string";
}

export function isCustomRange(range: TimeRangeMode): range is CustomRange {
  return typeof range !== "string";
}

/** The wire format for a {@link CustomRange} bound. */
const ISO_DATE = "YYYY-MM-DD";

/**
 * Whether `value` is a real `YYYY-MM-DD` calendar date.
 */
function isCalendarDate(value: unknown): value is string {
  return typeof value === "string" && dayjs(value, ISO_DATE, true).isValid();
}

/**
 * Parse an untrusted value — a URL param, a stored definition's `range` — into
 * a {@link TimeRangeMode}, or `null` if it is neither a known preset nor a
 * well-formed custom window.
 *
 * Takes `unknown` because the persisted value is no longer always a string.
 * Bounds are compared lexicographically, which is ordering-correct for
 * zero-padded ISO dates; `start === end` is a legal one-day window.
 */
export function asTimeRangeMode(value: unknown): TimeRangeMode | null {
  if (typeof value === "string") {
    return TIME_RANGES.some((t) => t.value === value)
      ? (value as PresetRange)
      : null;
  }
  if (
    isRecordValue(value) &&
    value.kind === "custom" &&
    isCalendarDate(value.start) &&
    isCalendarDate(value.end) &&
    value.start <= value.end
  ) {
    return { kind: "custom", start: value.start, end: value.end };
  }
  return null;
}

export function asPeriod(value: string | null): Period | null {
  return value !== null && (PERIODS as readonly string[]).includes(value)
    ? (value as Period)
    : null;
}

function asAggregation(value: string | null): NumericAggregation | null {
  return value !== null &&
    (NUMERIC_AGGREGATIONS as readonly string[]).includes(value)
    ? (value as NumericAggregation)
    : null;
}

/**
 * Placement cells (and spans) must be sane positive integers. The cap guards
 * the layout-packing loops in `placedLayout.ts`, which iterate over every
 * occupied cell — a definition is rejected outright rather than accepted
 * with a span that could hang the packer.
 */
const MAX_PLACEMENT_CELLS = 1000;

function isRecordValue(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isPlacementCell(value: unknown): boolean {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= MAX_PLACEMENT_CELLS
  );
}

function isPlacement(value: unknown): value is Placement {
  return (
    isRecordValue(value) &&
    isPlacementCell(value.row) &&
    isPlacementCell(value.col) &&
    isPlacementCell(value.w) &&
    isPlacementCell(value.h)
  );
}

function hasValidAggregation(value: Record<string, unknown>): boolean {
  return (
    asAggregation(
      typeof value.aggregation === "string" ? value.aggregation : null,
    ) !== null
  );
}

/**
 * The CANONICAL group-by paths the dashboard's Group by picker can produce:
 * the device id, and a device's or session's custom field.
 *
 * Canonical, and only canonical. Group-by resolution on the backend does NOT
 * go through the RoboQL catalog, so it performs no alias resolution: the
 * catalog treats `device.id` as a synonym of `device.device_id` for
 * FILTERING, but the aggregate endpoint rejects it. Accepting an alias here
 * would let a definition validate on the client and then 400 on every one of
 * that card's requests. Nothing between the picker and the request rewrites
 * the path, so what is stored is what the picker offered.
 *
 * The custom-field segment mirrors the backend's own
 * `CUSTOM_FIELD_NAME_PATTERN` (`[a-z][a-z0-9_]{0,62}`).
 */
const GROUP_BY_PATH =
  /^(device\.device_id|(device|session)\.custom\.[a-z][a-z0-9_]{0,62})$/;

/**
 * Whether a card's `group_by` is acceptable: absent, or one of the canonical
 * paths above.
 *
 * Absence matters most — every dashboard saved before this field existed
 * carries none, and this validator gates the WHOLE definition, so rejecting
 * the absence would make each of them read as corrupt and silently delete its
 * draft. What is checked when it IS present is the path's SHAPE, not
 * membership of the org's groupable set: that set includes the org's Ready
 * custom fields, so checking it here would make a dashboard grouped by a
 * later-deleted field unloadable rather than merely wrong on one card.
 */
function hasValidGroupBy(value: Record<string, unknown>): boolean {
  return (
    value.group_by === undefined ||
    (typeof value.group_by === "string" && GROUP_BY_PATH.test(value.group_by))
  );
}

function isCardConfig(value: unknown): value is CardConfig {
  if (
    !isRecordValue(value) ||
    typeof value.id !== "string" ||
    (value.metric !== null && typeof value.metric !== "string") ||
    (value.placement !== undefined && !isPlacement(value.placement)) ||
    (value.section !== undefined && typeof value.section !== "string")
  ) {
    return false;
  }
  if (value.kind === "trend") {
    return hasValidAggregation(value);
  }
  if (value.kind !== "chart") {
    return false;
  }
  // Checked before the raw early-return below, so a malformed group-by is
  // rejected on either chart variant rather than riding in on the one whose
  // absent aggregation ends the checks.
  if (!hasValidGroupBy(value)) {
    return false;
  }
  // A RAW chart card ({@link RawChartCardConfig}) carries neither field, and
  // that absence is exactly what identifies it. Tolerating it here — rather
  // than demanding an aggregation of every chart, as this validator used to —
  // is what keeps a dashboard containing one from reading as corrupt, which
  // deletes drafts silently and offers to delete the saved record.
  //
  // Tolerance is for ABSENCE only: a card that names an aggregation or a
  // display we cannot honour is still a broken definition, and rejecting it
  // is what keeps accepted input safe on every path past this point.
  if (value.aggregation === undefined && value.display === undefined) {
    return true;
  }
  return (
    hasValidAggregation(value) &&
    CHART_DISPLAY_TYPES.some((d) => d.value === value.display)
  );
}

function isSectionConfig(value: unknown): value is SectionConfig {
  return (
    isRecordValue(value) &&
    typeof value.id === "string" &&
    typeof value.title === "string"
  );
}

/**
 * Strict structural check for a {@link DashboardDefinition} loaded from
 * outside the running app (a backend record's JSONB, a localStorage draft).
 * Only the current schema shape passes — including its `range`/`period`
 * enums, filter shape, card kinds, and placement bounds — so accepted input
 * can never reach a code path that breaks after acceptance, and rejected
 * input takes the caller's invalid-input fallback instead of loading
 * half-broken (e.g. an unknown range that would read back as a
 * permanently-dirty dashboard).
 */
export function isDashboardDefinition(
  value: unknown,
): value is DashboardDefinition {
  return (
    isRecordValue(value) &&
    value.schema_version === DASHBOARD_SCHEMA_VERSION &&
    asTimeRangeMode(value.range) !== null &&
    typeof value.period === "string" &&
    asPeriod(value.period) !== null &&
    (value.filters === undefined ||
      savedFiltersSchema.safeParse(value.filters).success) &&
    Array.isArray(value.sections) &&
    value.sections.every(isSectionConfig) &&
    Array.isArray(value.cards) &&
    value.cards.every(isCardConfig)
  );
}
